import { create } from 'zustand';
import { immer } from 'zustand/middleware/immer';
import {
  SpineImportState,
  SpineImportActions,
  SpineRuntimeState,
  SpineRuntimeActions,
  SpineFileSet,
  SpineFileInfo,
  SpineAsset,
  SpineInstance,
  SpineEvent,
  detectSpineFileType,
  getBaseNameFromFile,
  validateSpineFileSet,
  DEFAULT_IMPORT_SCALE,
} from '../../core/types/spine.types';
import { useProjectPanelStore } from './projectPanelStore';

const generateId = () => Math.random().toString(36).substring(2, 11);

// ============================================================================
// SPINE IMPORT STORE
// ============================================================================

interface SpineImportStore extends SpineImportState, SpineImportActions {}

const initialImportState: SpineImportState = {
  isOpen: false,
  fileSets: [],
  selectedFileSetId: null,
  previewAsset: null,
  previewAnimation: null,
  isPreviewPlaying: false,
  isImporting: false,
  importProgress: {
    current: 0,
    total: 0,
    currentFile: '',
    status: 'idle',
  },
  importedAssets: [],
  errors: [],
};

export const useSpineImportStore = create<SpineImportStore>()(
  immer((set, get) => ({
    ...initialImportState,

    // Dialog control
    openDialog: () => {
      set((state) => {
        state.isOpen = true;
        state.fileSets = [];
        state.selectedFileSetId = null;
        state.errors = [];
        state.importProgress.status = 'idle';
      });
    },

    closeDialog: () => {
      set((state) => {
        state.isOpen = false;
        // Clear preview URLs
        state.fileSets.forEach((fileSet) => {
          // Revoke object URLs if any
        });
        state.fileSets = [];
        state.previewAsset = null;
      });
    },

    // File management
    addFiles: (files: FileList | File[]) => {
      const fileArray = Array.from(files);

      set((state) => {
        // Group files by base name
        const fileGroups: Record<string, SpineFileInfo[]> = {};

        for (const file of fileArray) {
          const fileType = detectSpineFileType(file);
          if (!fileType) continue;

          const baseName = getBaseNameFromFile(file.name);

          if (!fileGroups[baseName]) {
            fileGroups[baseName] = [];
          }

          fileGroups[baseName].push({
            id: generateId(),
            file,
            name: file.name,
            extension: '.' + file.name.split('.').pop()?.toLowerCase(),
            size: file.size,
            type: fileType,
            isValid: true,
          });
        }

        // Create or update file sets
        for (const [baseName, groupFiles] of Object.entries(fileGroups)) {
          // Find existing file set or create new one
          let fileSet = state.fileSets.find((fs) => fs.name === baseName);

          if (!fileSet) {
            fileSet = {
              id: generateId(),
              name: baseName,
              skeletonFile: null,
              atlasFile: null,
              textureFiles: [],
              isComplete: false,
              isValid: false,
              errors: [],
              warnings: [],
              importScale: DEFAULT_IMPORT_SCALE,
              targetFolder: 'assets/spine',
              generatePrefab: true,
              prefabName: baseName,
            };
            state.fileSets.push(fileSet);
          }

          // Assign files to appropriate slots
          for (const fileInfo of groupFiles) {
            if (fileInfo.type === 'skeleton') {
              fileSet.skeletonFile = fileInfo;
            } else if (fileInfo.type === 'atlas') {
              fileSet.atlasFile = fileInfo;
            } else if (fileInfo.type === 'texture') {
              // Avoid duplicates
              if (!fileSet.textureFiles.find((tf) => tf.name === fileInfo.name)) {
                fileSet.textureFiles.push(fileInfo);
              }
            }
          }

          // Validate file set
          fileSet.errors = validateSpineFileSet(fileSet);
          fileSet.isComplete = fileSet.errors.length === 0;
          fileSet.isValid = fileSet.isComplete;
        }

        // Auto-select first file set
        if (state.fileSets.length > 0 && !state.selectedFileSetId) {
          state.selectedFileSetId = state.fileSets[0].id;
        }
      });
    },

    removeFileSet: (id: string) => {
      set((state) => {
        state.fileSets = state.fileSets.filter((fs) => fs.id !== id);
        if (state.selectedFileSetId === id) {
          state.selectedFileSetId = state.fileSets[0]?.id || null;
        }
      });
    },

    clearFiles: () => {
      set((state) => {
        state.fileSets = [];
        state.selectedFileSetId = null;
        state.previewAsset = null;
      });
    },

    selectFileSet: (id: string | null) => {
      set((state) => {
        state.selectedFileSetId = id;
        state.previewAsset = null;
        state.previewAnimation = null;
      });
    },

    // Settings per file set
    updateFileSet: (id: string, updates: Partial<SpineFileSet>) => {
      set((state) => {
        const fileSet = state.fileSets.find((fs) => fs.id === id);
        if (fileSet) {
          Object.assign(fileSet, updates);
        }
      });
    },

    setImportScale: (id: string, scale: number) => {
      set((state) => {
        const fileSet = state.fileSets.find((fs) => fs.id === id);
        if (fileSet) {
          fileSet.importScale = Math.max(0.01, Math.min(10, scale));
        }
      });
    },

    setTargetFolder: (id: string, folder: string) => {
      set((state) => {
        const fileSet = state.fileSets.find((fs) => fs.id === id);
        if (fileSet) {
          fileSet.targetFolder = folder;
        }
      });
    },

    setGeneratePrefab: (id: string, generate: boolean) => {
      set((state) => {
        const fileSet = state.fileSets.find((fs) => fs.id === id);
        if (fileSet) {
          fileSet.generatePrefab = generate;
        }
      });
    },

    // Preview
    setPreviewAnimation: (name: string | null) => {
      set((state) => {
        state.previewAnimation = name;
      });
    },

    togglePreviewPlaying: () => {
      set((state) => {
        state.isPreviewPlaying = !state.isPreviewPlaying;
      });
    },

    // Import
    validateFiles: async () => {
      const { fileSets } = get();

      set((state) => {
        state.importProgress.status = 'validating';
      });

      for (const fileSet of fileSets) {
        set((state) => {
          const fs = state.fileSets.find((f) => f.id === fileSet.id);
          if (fs) {
            fs.errors = validateSpineFileSet(fs);
            fs.isValid = fs.errors.length === 0;

            // Parse skeleton to get metadata
            // This would use spine-loader.ts utilities
          }
        });
      }

      set((state) => {
        state.importProgress.status = 'idle';
      });
    },

    startImport: async () => {
      const { fileSets } = get();
      const validFileSets = fileSets.filter((fs) => fs.isValid);

      if (validFileSets.length === 0) {
        set((state) => {
          state.errors = ['No valid Spine file sets to import'];
        });
        return;
      }

      set((state) => {
        state.isImporting = true;
        state.importProgress = {
          current: 0,
          total: validFileSets.length,
          currentFile: '',
          status: 'copying',
        };
        state.errors = [];
        state.importedAssets = [];
      });

      const projectPanelStore = useProjectPanelStore.getState();

      for (let i = 0; i < validFileSets.length; i++) {
        const fileSet = validFileSets[i];

        set((state) => {
          state.importProgress.current = i + 1;
          state.importProgress.currentFile = fileSet.name;
        });

        try {
          // Create folder structure
          const folderPath = fileSet.targetFolder + '/' + fileSet.name;

          // Copy files to project
          const allFiles: File[] = [];
          if (fileSet.skeletonFile) allFiles.push(fileSet.skeletonFile.file);
          if (fileSet.atlasFile) allFiles.push(fileSet.atlasFile.file);
          allFiles.push(...fileSet.textureFiles.map((tf) => tf.file));

          // Use project panel store to import files
          await projectPanelStore.importExternalFiles(allFiles, folderPath);

          // Create SpineAsset metadata
          const spineAsset: SpineAsset = {
            id: generateId(),
            name: fileSet.name,
            path: folderPath,
            skeletonPath: folderPath + '/' + fileSet.skeletonFile!.name,
            atlasPath: folderPath + '/' + fileSet.atlasFile!.name,
            texturePaths: fileSet.textureFiles.map((tf) => folderPath + '/' + tf.name),
            version: fileSet.version || '4.1',
            width: 0,
            height: 0,
            bones: [],
            slots: [],
            skins: [{ name: 'default', attachments: {} }],
            animations: [],
            events: [],
            importScale: fileSet.importScale,
            importedAt: Date.now(),
          };

          // If skins/animations were detected during validation, use them
          if (fileSet.skinNames) {
            spineAsset.skins = fileSet.skinNames.map((name) => ({
              name,
              attachments: {},
            }));
          }
          if (fileSet.animationNames) {
            spineAsset.animations = fileSet.animationNames.map((name) => ({
              name,
              duration: 0,
              boneTimelines: [],
              slotTimelines: [],
              hasEvents: false,
              eventNames: [],
            }));
          }

          set((state) => {
            state.importedAssets.push(spineAsset);
          });

          // Generate prefab if requested
          if (fileSet.generatePrefab) {
            // Create prefab with SpineRenderer and SpineAnimator components
            const prefabData = {
              name: fileSet.prefabName || fileSet.name,
              type: 'spine' as const,
              components: [
                {
                  type: 'spine-renderer',
                  data: {
                    spineAssetId: spineAsset.id,
                    spineAssetPath: spineAsset.path,
                    skinName: 'default',
                    tint: '#ffffff',
                    alpha: 1,
                    premultipliedAlpha: true,
                    sortingLayer: 'default',
                    sortingOrder: 0,
                    showBones: false,
                    showSlots: false,
                    showBoundingBoxes: false,
                  },
                },
                {
                  type: 'spine-animator',
                  data: {
                    playOnAwake: false,
                    defaultAnimation: spineAsset.animations[0]?.name || '',
                    defaultLoop: true,
                    speed: 1,
                    defaultMixDuration: 0.2,
                    mixDurations: {},
                    currentAnimation: '',
                    isPlaying: false,
                    currentTime: 0,
                    enableEvents: true,
                    showAnimationList: true,
                  },
                },
              ],
            };

            // Save prefab to project
            await projectPanelStore.createAsset(
              'prefab',
              folderPath,
              fileSet.prefabName || fileSet.name + '.prefab'
            );
          }
        } catch (error) {
          set((state) => {
            state.errors.push(`Failed to import ${fileSet.name}: ${error}`);
          });
        }
      }

      set((state) => {
        state.isImporting = false;
        state.importProgress.status = 'complete';
      });
    },

    cancelImport: () => {
      set((state) => {
        state.isImporting = false;
        state.importProgress.status = 'idle';
      });
    },

    addImportedAsset: (asset: SpineAsset) => {
      set((state) => {
        // Avoid duplicates
        if (!state.importedAssets.find((a) => a.id === asset.id)) {
          state.importedAssets.push(asset);
        }
      });
    },
  }))
);

// ============================================================================
// SPINE RUNTIME STORE
// ============================================================================

interface SpineRuntimeStore extends SpineRuntimeState, SpineRuntimeActions {}

const initialRuntimeState: SpineRuntimeState = {
  instances: {},
  loadedAssets: {},
};

export const useSpineRuntimeStore = create<SpineRuntimeStore>()(
  immer((set, get) => ({
    ...initialRuntimeState,

    // Instance management
    createInstance: (gameObjectId: string, assetId: string): string => {
      const id = generateId();

      set((state) => {
        state.instances[id] = {
          id,
          gameObjectId,
          assetId,
          skeleton: null,
          animationState: null,
          currentAnimation: null,
          currentSkin: 'default',
          timeScale: 1,
          isPlaying: false,
        };
      });

      return id;
    },

    destroyInstance: (instanceId: string) => {
      set((state) => {
        delete state.instances[instanceId];
      });
    },

    // Asset loading
    loadAsset: async (assetId: string): Promise<SpineAsset> => {
      const { loadedAssets } = get();

      if (loadedAssets[assetId]) {
        return loadedAssets[assetId];
      }

      // Check if we have a partially-registered asset with paths
      // (registered via registerAssetFromProject but not fully loaded)
      // For now, create a placeholder — metadata will be enriched
      // by SpineOffscreenRenderer.extractMetadata() when available
      const asset: SpineAsset = {
        id: assetId,
        name: 'placeholder',
        path: '',
        skeletonPath: '',
        atlasPath: '',
        texturePaths: [],
        version: '4.1',
        width: 0,
        height: 0,
        bones: [],
        slots: [],
        skins: [],
        animations: [],
        events: [],
        importScale: 1,
        importedAt: Date.now(),
      };

      set((state) => {
        state.loadedAssets[assetId] = asset;
      });

      return asset;
    },

    unloadAsset: (assetId: string) => {
      set((state) => {
        delete state.loadedAssets[assetId];
      });
    },

    // Animation control
    play: (instanceId: string, animationName: string, loop: boolean = true) => {
      set((state) => {
        const instance = state.instances[instanceId];
        if (instance) {
          instance.currentAnimation = animationName;
          instance.isPlaying = true;
          // Would trigger actual Spine runtime play
        }
      });
    },

    stop: (instanceId: string) => {
      set((state) => {
        const instance = state.instances[instanceId];
        if (instance) {
          instance.isPlaying = false;
          instance.currentAnimation = null;
        }
      });
    },

    pause: (instanceId: string) => {
      set((state) => {
        const instance = state.instances[instanceId];
        if (instance) {
          instance.isPlaying = false;
        }
      });
    },

    resume: (instanceId: string) => {
      set((state) => {
        const instance = state.instances[instanceId];
        if (instance && instance.currentAnimation) {
          instance.isPlaying = true;
        }
      });
    },

    setTimeScale: (instanceId: string, scale: number) => {
      set((state) => {
        const instance = state.instances[instanceId];
        if (instance) {
          instance.timeScale = Math.max(0, Math.min(5, scale));
        }
      });
    },

    // Skin control
    setSkin: (instanceId: string, skinName: string) => {
      set((state) => {
        const instance = state.instances[instanceId];
        if (instance) {
          instance.currentSkin = skinName;
          // Would trigger actual Spine runtime skin change
        }
      });
    },

    // Mixing
    setMixDuration: (
      instanceId: string,
      fromAnim: string,
      toAnim: string,
      duration: number
    ) => {
      // Would configure Spine AnimationStateData
      console.log(`Set mix duration ${fromAnim} -> ${toAnim}: ${duration}s`);
    },

    // Events
    onEvent: (instanceId: string, callback: (event: SpineEvent) => void): (() => void) => {
      // Would register event listener on Spine AnimationState
      return () => {
        // Cleanup
      };
    },

    onComplete: (instanceId: string, callback: (animation: string) => void): (() => void) => {
      // Would register complete listener on Spine AnimationState
      return () => {
        // Cleanup
      };
    },

    registerAssetFromProject: (
      name: string,
      skeletonPath: string,
      atlasPath: string,
      texturePaths: string[],
      metadata?: Partial<SpineAsset>
    ): SpineAsset => {
      const id = `spine-project-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
      const folderPath = skeletonPath.split('/').slice(0, -1).join('/');

      const asset: SpineAsset = {
        id,
        name,
        path: folderPath,
        skeletonPath,
        atlasPath,
        texturePaths,
        version: metadata?.version || '4.0',
        width: metadata?.width || 0,
        height: metadata?.height || 0,
        bones: metadata?.bones || [],
        slots: metadata?.slots || [],
        skins: metadata?.skins || [{ name: 'default', attachments: {} }],
        animations: metadata?.animations || [],
        events: metadata?.events || [],
        importScale: 1,
        importedAt: Date.now(),
      };

      set((state) => {
        state.loadedAssets[id] = asset;
      });

      // Also add to import store so it appears in dropdown
      useSpineImportStore.getState().addImportedAsset(asset);

      // Async: enrich metadata using the real spine runtime parser
      // This populates accurate bone/slot/skin/animation data from binary .skel files
      import('../services/SpineOffscreenRenderer').then(({ spineOffscreenRenderer }) => {
        spineOffscreenRenderer
          .extractMetadata(skeletonPath, atlasPath, texturePaths)
          .then((meta) => {
            set((state) => {
              const existing = state.loadedAssets[id];
              if (existing) {
                existing.width = meta.width || existing.width;
                existing.height = meta.height || existing.height;
                existing.skins = meta.skins.map((s) => ({ name: s, attachments: {} }));
                existing.animations = meta.animations.map((a) => ({
                  name: a.name,
                  duration: a.duration,
                  boneTimelines: [],
                  slotTimelines: [],
                  hasEvents: false,
                  eventNames: [],
                }));
                existing.bones = meta.bones.map((b) => ({
                  name: b.name,
                  parentName: b.parent,
                  length: 0,
                  rotation: 0,
                  x: 0,
                  y: 0,
                  scaleX: 1,
                  scaleY: 1,
                }));
                existing.slots = meta.slots.map((s) => ({
                  name: s.name,
                  boneName: s.bone,
                  color: 'ffffffff',
                  attachment: null,
                  blendMode: 'normal' as const,
                }));
              }
            });
          })
          .catch((err) => {
            console.warn(`[SpineStore] Failed to extract metadata for ${name}:`, err);
          });
      });

      return asset;
    },
  }))
);
